import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { MdRefresh, MdDelete, MdCloudUpload, MdEdit, MdPause, MdPlayArrow, MdCheckCircle, MdFileDownload, MdFileUpload } from "react-icons/md";
import { toast } from "react-toastify";
import { useVisitQueue, clasificarRespuestaVisita } from "../../../providers/visitQueueContext";
import { importarBackup, construirBackupBlob, descargarBlobLocal } from "../../../utilities/visitBackup";
import { importVisitSheetZip } from "../../../services/visitsheets.service";

// A partir del último error guardado en la entrada, deduce el "resultado" que
// espera el backup (sólo para rellenar el detalle del fallo en el ZIP).
const resultadoDesdeEntry = (entry) => {
  const e = entry?.lastError;
  if (!e) return null;
  const tipo =
    e.kind === "client" ? "cliente" : e.kind === "network" ? "red" : "servidor";
  return { tipo, code: e.status ?? null, error: e };
};

const ColorButton = styled(Button)(() => ({
  color: "white",
  backgroundColor: "#92004D",
  "&:hover": { backgroundColor: "#7a0040" },
}));

const formatearFecha = (ts) => {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch (e) {
    return "";
  }
};

const formatearRestante = (ms, t) => {
  if (ms <= 0) {
    return t("visitQueue.soon", { defaultValue: "en breve" });
  }
  const totalSeg = Math.ceil(ms / 1000);
  const min = Math.floor(totalSeg / 60);
  const seg = totalSeg % 60;
  if (min > 0) return `${min} min ${seg.toString().padStart(2, "0")} s`;
  return `${seg} s`;
};

const EstadoChip = ({ status, paused, t }) => {
  if (status === "error") {
    return (
      <Chip
        label={t("visitQueue.status.error", { defaultValue: "Error" })}
        size="small"
        sx={{ backgroundColor: "#fdecea", color: "#b71c1c", fontWeight: 600 }}
      />
    );
  }
  if (paused) {
    return (
      <Chip
        label={t("visitQueue.status.paused", { defaultValue: "En pausa" })}
        size="small"
        sx={{ backgroundColor: "#eeeeee", color: "#555", fontWeight: 600 }}
      />
    );
  }
  if (status === "processing") {
    return (
      <Chip
        label={t("visitQueue.status.processing", { defaultValue: "Enviando..." })}
        size="small"
        sx={{ backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: 600 }}
      />
    );
  }
  return (
    <Chip
      label={t("visitQueue.status.pending", { defaultValue: "En cola" })}
      size="small"
      sx={{ backgroundColor: "#fff8e1", color: "#8d6e00", fontWeight: 600 }}
    />
  );
};

function ColaVisitas() {
  const { t } = useTranslation(["properties", "common"]);
  const navigate = useNavigate();
  const {
    entries,
    errorCount,
    pausedAll,
    sentEntries,
    enqueue,
    processEntry,
    processAll,
    removeEntry,
    setPausedEntry,
    togglePauseAll,
    addSent,
    removeSent,
    clearSent,
  } = useVisitQueue();
  const [procesando, setProcesando] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [ahora, setAhora] = useState(Date.now());
  const [entradaABorrar, setEntradaABorrar] = useState(null);
  const [importando, setImportando] = useState(false);
  const [backupId, setBackupId] = useState(null);
  const importInputRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleForzar = async (id, esError) => {
    setAccionId(id);
    try {
      await processEntry(id, { force: esError });
    } finally {
      setAccionId(null);
    }
  };

  const handleForzarTodos = async () => {
    setProcesando(true);
    try {
      await processAll({ includeErrors: true });
    } finally {
      setProcesando(false);
    }
  };

  const handleImportarClick = () => importInputRef.current?.click();

  const handleImportarBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite reimportar el mismo archivo
    if (!file) return;
    setImportando(true);
    try {
      // Reconstruimos la entrada (id/params/datos) para poder encolar o quitar
      // de la cola según el resultado del intento directo.
      const entry = await importarBackup(file);
      const nif = entry.params?.nif;
      if (!nif) {
        toast.error(
          t("visitQueue.toast.importError", {
            defaultValue:
              "No se pudo importar el backup. Comprueba que es un .zip de backup válido.",
          })
        );
        return;
      }

      // Intento directo: subimos el .zip tal cual al back (un único binario).
      let res;
      try {
        res = await importVisitSheetZip(file, nif);
      } catch (err) {
        res = undefined;
      }
      const r = clasificarRespuestaVisita(res);

      if (r.tipo === "exito") {
        // Creada en el servidor: la quitamos de la cola y la marcamos enviada.
        try {
          await removeEntry(entry.id);
        } catch (e2) {
          /* no estaba en la cola: nada que quitar */
        }
        addSent({
          id: entry.id,
          comunidad: entry.comunidad,
          tecnicoPRL: entry.tecnicoPRL,
          sentAt: Date.now(),
        });
        toast.success(
          t("visitQueue.toast.imported", {
            defaultValue: "Backup importado: la visita se ha creado correctamente.",
          })
        );
        return;
      }

      if (r.tipo === "cliente" && r.code === 409) {
        try {
          await removeEntry(entry.id);
        } catch (e2) {
          /* noop */
        }
        toast.info(
          t("visitQueue.toast.importExists", {
            defaultValue:
              "Esta visita ya existía en el servidor. Se ha quitado de la cola.",
          })
        );
        return;
      }

      // No se pudo crear ahora (red/servidor u otro error): la guardamos en la
      // cola para no perderla y que se reintente automáticamente.
      await enqueue(entry);
      if (r.tipo === "cliente") {
        toast.warning(
          t("visitQueue.toast.importQueuedClient", {
            defaultValue:
              "No se pudo crear la visita ({{motivo}}). Se ha guardado en la cola.",
            motivo: r.error?.message || "",
          })
        );
      } else {
        toast.warning(
          t("visitQueue.toast.importQueued", {
            defaultValue:
              "No se pudo crear ahora. Se ha guardado en la cola y se reintentará automáticamente.",
          })
        );
      }
    } catch (err) {
      console.error("Error importando backup:", err);
      toast.error(
        t("visitQueue.toast.importError", {
          defaultValue:
            "No se pudo importar el backup. Comprueba que es un .zip de backup válido.",
        })
      );
    } finally {
      setImportando(false);
    }
  };

  const handleDescargarBackup = async (entry) => {
    setBackupId(entry.id);
    try {
      const { blob, filename } = await construirBackupBlob(
        entry,
        resultadoDesdeEntry(entry)
      );
      await descargarBlobLocal(blob, filename);
    } catch (err) {
      console.error("Error generando el backup:", err);
      toast.error(
        t("visitQueue.toast.backupError", {
          defaultValue: "No se pudo generar el backup.",
        })
      );
    } finally {
      setBackupId(null);
    }
  };

  const handleEditar = (entry) => {
    const { nif, id, idCentroTrabajo } = entry.params || {};
    const path = idCentroTrabajo
      ? `/hojavisita/${nif}/${idCentroTrabajo}/create/${id}`
      : `/hojavisita/${nif}/create/${id}`;
    navigate(path);
  };

  const confirmarEliminar = async () => {
    if (!entradaABorrar) return;
    const id = entradaABorrar.id;
    setAccionId(id);
    try {
      await removeEntry(id);
      toast.info(
        t("visitQueue.toast.deleted", {
          defaultValue: "Hoja de visita eliminada de la cola",
        })
      );
    } finally {
      setAccionId(null);
      setEntradaABorrar(null);
    }
  };

  return (
    <>
      <Box className="pageHeader" sx={{ backgroundColor: "#f6f7fb" }}>
        <Typography
          variant="h4"
          color="#212529"
          fontWeight={"bold"}
          sx={{ fontSize: { xs: "1.4rem", md: "2.125rem" } }}
        >
          {t("visitQueue.title", { defaultValue: "Cola de visitas" })}
        </Typography>
      </Box>
      <Box className="pageContent centered" sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" color="#555">
            {t("visitQueue.subtitle", {
              defaultValue:
                "Hojas de visita pendientes de envío. Se reintentan automáticamente cada pocos minutos y al recuperar la conexión.",
            })}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            <input
              ref={importInputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              style={{ display: "none" }}
              onChange={handleImportarBackup}
            />
            <Button
              onClick={handleImportarClick}
              disabled={importando}
              variant="outlined"
              startIcon={
                importando ? (
                  <CircularProgress size={18} sx={{ color: "#92004D" }} />
                ) : (
                  <MdFileUpload />
                )
              }
              sx={{
                whiteSpace: "nowrap",
                textTransform: "none",
                color: "#92004D",
                borderColor: "#92004D",
                "&:hover": { borderColor: "#7a0040", backgroundColor: "#faf5f8" },
              }}
            >
              {t("visitQueue.importBackup", { defaultValue: "Importar backup" })}
            </Button>
            {entries.length > 0 && (
              <>
                <Button
                  onClick={togglePauseAll}
                  variant="outlined"
                  startIcon={pausedAll ? <MdPlayArrow /> : <MdPause />}
                  sx={{
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    color: "#92004D",
                    borderColor: "#92004D",
                    "&:hover": { borderColor: "#7a0040", backgroundColor: "#faf5f8" },
                  }}
                >
                  {pausedAll
                    ? t("visitQueue.resumeAll", { defaultValue: "Reanudar cola" })
                    : t("visitQueue.pauseAll", { defaultValue: "Pausar cola" })}
                </Button>
                <ColorButton
                  onClick={handleForzarTodos}
                  disabled={procesando}
                  sx={{ whiteSpace: "nowrap" }}
                  startIcon={
                    procesando ? (
                      <CircularProgress size={18} sx={{ color: "white" }} />
                    ) : (
                      <MdCloudUpload />
                    )
                  }
                >
                  {t("visitQueue.forceAll", { defaultValue: "Reintentar todas" })}
                </ColorButton>
              </>
            )}
          </Stack>
        </Box>

        {pausedAll && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#eeeeee",
              border: "1px solid #ccc",
              borderRadius: "8px",
            }}
          >
            <Typography variant="body2" color="#555">
              {t("visitQueue.pausedBanner", {
                defaultValue:
                  "La cola está en pausa: no se reintentará automáticamente. Puedes reanudarla o reintentar manualmente.",
              })}
            </Typography>
          </Box>
        )}

        {errorCount > 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#fdecea",
              border: "1px solid #f5c6cb",
              borderRadius: "8px",
            }}
          >
            <Typography variant="body2" color="#b71c1c">
              {t("visitQueue.errorBanner", {
                count: errorCount,
                defaultValue:
                  "Hay hojas que no se pudieron enviar (no es un problema puntual de conexión). Revisa el detalle de cada una: si es un fallo de datos (ya existe, no se encuentra...) corrígelo o elimínala; si es del servidor, reinténtalo o contacta con soporte.",
              })}
            </Typography>
          </Box>
        )}

        {entries.length === 0 && sentEntries.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              border: "1px dashed #ccc",
              borderRadius: "12px",
            }}
          >
            <Typography variant="body1" color="#888">
              {t("visitQueue.empty", {
                defaultValue: "No hay hojas de visita pendientes en la cola.",
              })}
            </Typography>
          </Paper>
        ) : entries.length > 0 ? (
          <Stack spacing={2}>
            {entries.map((entry) => {
              const esError = entry.status === "error";
              const esPendiente = entry.status === "pending";
              const estaPausada = !!entry.paused;
              const enAccion = accionId === entry.id;
              const totalImgs =
                (entry.images?.length || 0) + (entry.mainImage ? 1 : 0);
              return (
                <Paper
                  key={entry.id}
                  elevation={1}
                  sx={{
                    p: { xs: 1.75, md: 2.5 },
                    borderRadius: "12px",
                    borderLeft: `5px solid ${esError ? "#b71c1c" : "#92004D"}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}>
                          {entry.comunidad ||
                            t("visitQueue.unnamed", {
                              defaultValue: "Hoja de visita",
                            })}
                        </Typography>
                        <EstadoChip status={entry.status} paused={estaPausada} t={t} />
                      </Box>
                      {entry.tecnicoPRL && (
                        <Typography variant="body2" color="#666">
                          {entry.tecnicoPRL}
                        </Typography>
                      )}
                      <Typography variant="caption" color="#888" display="block">
                        {t("visitQueue.createdAt", { defaultValue: "Creada" })}:{" "}
                        {formatearFecha(entry.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="#888" display="block">
                        {t("visitQueue.attempts", { defaultValue: "Intentos" })}:{" "}
                        {entry.attempts || 0}
                        {entry.serverAttempts
                          ? ` (${entry.serverAttempts} ${t(
                              "visitQueue.serverErrors",
                              { defaultValue: "errores de servidor" }
                            )})`
                          : ""}
                      </Typography>
                      <Typography variant="caption" color="#888" display="block">
                        {t("visitQueue.images", { defaultValue: "Imágenes" })}:{" "}
                        {totalImgs}
                      </Typography>
                      {esPendiente && !estaPausada && !pausedAll && entry.nextRetryAt && (
                        <Typography
                          variant="caption"
                          color="#1565c0"
                          display="block"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                        >
                          {t("visitQueue.nextRetry", {
                            defaultValue: "Próximo reintento",
                          })}
                          : {formatearRestante(entry.nextRetryAt - ahora, t)}
                        </Typography>
                      )}
                      {esPendiente && (estaPausada || pausedAll) && (
                        <Typography
                          variant="caption"
                          color="#777"
                          display="block"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                        >
                          {estaPausada
                            ? t("visitQueue.pausedEntryNote", {
                                defaultValue: "Pausada: no se reintentará hasta reanudarla.",
                              })
                            : t("visitQueue.pausedBannerShort", {
                                defaultValue: "Cola en pausa.",
                              })}
                        </Typography>
                      )}
                      {esError && (
                        <Typography
                          variant="caption"
                          color="#b71c1c"
                          display="block"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                        >
                          {entry.lastError?.kind === "client"
                            ? t("visitQueue.clientErrorNote", {
                                defaultValue:
                                  "Fallo de datos: no se puede subir. Corrige la visita o elimínala (reintentar no lo resolverá).",
                              })
                            : t("visitQueue.noAutoRetry", {
                                defaultValue:
                                  "No se reintentará automáticamente. Pulsa Reintentar.",
                              })}
                        </Typography>
                      )}
                      {entry.lastError?.message && (
                        <Typography
                          variant="caption"
                          color={esError ? "#b71c1c" : "#8d6e00"}
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {t("visitQueue.lastError", {
                            defaultValue: "Último error",
                          })}
                          : {entry.lastError.message}
                          {entry.lastError.kind === "network"
                            ? ` (${t("visitQueue.networkError", {
                                defaultValue: "conexión",
                              })})`
                            : ""}
                        </Typography>
                      )}
                    </Box>

                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{
                        width: { xs: "100%", md: 160 },
                        flexShrink: 0,
                        mt: { xs: 1.5, md: 0 },
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        disableElevation
                        onClick={() => handleForzar(entry.id, esError)}
                        disabled={enAccion || entry.status === "processing"}
                        startIcon={
                          enAccion ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                          ) : (
                            <MdRefresh />
                          )
                        }
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          backgroundColor: "#92004D",
                          "&:hover": { backgroundColor: "#7a0040" },
                        }}
                      >
                        {t("visitQueue.force", { defaultValue: "Reintentar" })}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => handleEditar(entry)}
                        disabled={enAccion || entry.status === "processing"}
                        startIcon={<MdEdit />}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "#444",
                          borderColor: "#bbb",
                          "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" },
                        }}
                      >
                        {t("visitQueue.edit", { defaultValue: "Editar" })}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => setPausedEntry(entry.id, !estaPausada)}
                        disabled={enAccion || entry.status === "processing"}
                        startIcon={estaPausada ? <MdPlayArrow /> : <MdPause />}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "#444",
                          borderColor: "#bbb",
                          "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" },
                        }}
                      >
                        {estaPausada
                          ? t("visitQueue.resume", { defaultValue: "Reanudar" })
                          : t("visitQueue.pause", { defaultValue: "Pausar" })}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => handleDescargarBackup(entry)}
                        disabled={backupId === entry.id}
                        startIcon={
                          backupId === entry.id ? (
                            <CircularProgress size={16} sx={{ color: "#444" }} />
                          ) : (
                            <MdFileDownload />
                          )
                        }
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "#444",
                          borderColor: "#bbb",
                          "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" },
                        }}
                      >
                        {t("visitQueue.downloadBackup", { defaultValue: "Descargar backup" })}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        color="error"
                        onClick={() => setEntradaABorrar(entry)}
                        disabled={enAccion}
                        startIcon={<MdDelete />}
                        sx={{ justifyContent: "flex-start", textTransform: "none" }}
                      >
                        {t("visitQueue.delete", { defaultValue: "Eliminar" })}
                      </Button>
                    </Stack>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        ) : null}

        {sentEntries.length > 0 && (
          <Box sx={{ mt: entries.length > 0 ? 4 : 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600} color="#2e7d32">
                {t("visitQueue.sentTitle", { defaultValue: "Enviadas correctamente" })}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={clearSent}
                startIcon={<MdDelete />}
                sx={{
                  textTransform: "none",
                  color: "#555",
                  borderColor: "#bbb",
                  alignSelf: { xs: "stretch", sm: "auto" },
                  "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" },
                }}
              >
                {t("visitQueue.clearSent", { defaultValue: "Limpiar enviadas" })}
              </Button>
            </Box>
            <Stack spacing={1.5}>
              {sentEntries.map((s) => (
                <Paper
                  key={s.id}
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    borderRadius: "12px",
                    border: "1px solid #c8e6c9",
                    backgroundColor: "#f1f8f4",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <MdCheckCircle color="#2e7d32" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {s.comunidad ||
                          t("visitQueue.unnamed", { defaultValue: "Hoja de visita" })}
                      </Typography>
                    </Box>
                    {s.tecnicoPRL && (
                      <Typography variant="body2" color="#666">
                        {s.tecnicoPRL}
                      </Typography>
                    )}
                    <Typography variant="caption" color="#2e7d32" display="block">
                      {t("visitQueue.sentAt", { defaultValue: "Enviada" })}:{" "}
                      {formatearFecha(s.sentAt)}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => removeSent(s.id)}
                    startIcon={<MdDelete />}
                    sx={{ textTransform: "none", color: "#777", flexShrink: 0 }}
                  >
                    {t("visitQueue.remove", { defaultValue: "Quitar" })}
                  </Button>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <Dialog
        open={!!entradaABorrar}
        onClose={() => accionId === null && setEntradaABorrar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#212529" }}>
          {t("visitQueue.deleteTitle", { defaultValue: "Eliminar de la cola" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#555" }}>
            {t("visitQueue.confirmDelete", {
              defaultValue:
                "¿Seguro que quieres eliminar esta hoja de visita de la cola? Se perderán los datos no enviados.",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <Button
            onClick={() => setEntradaABorrar(null)}
            disabled={accionId !== null}
            variant="outlined"
            fullWidth
            sx={{ color: "#444", borderColor: "#bbb", textTransform: "none" }}
          >
            {t("visitQueue.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            onClick={confirmarEliminar}
            disabled={accionId !== null}
            variant="contained"
            color="error"
            fullWidth
            disableElevation
            sx={{ textTransform: "none" }}
          >
            {accionId !== null ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("visitQueue.delete", { defaultValue: "Eliminar" })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ColaVisitas;
